import { useState, useMemo, useCallback, useEffect } from "react";
import { useTranslations } from "@/contexts/I18nContext";
import { doc, updateDoc, deleteDoc, addDoc, collection, Timestamp } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "@/lib/firebase";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useFirestoreCollection } from "@/hooks/useFirestore";
import { Card } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { MealCard } from "@/components/cards/MealCard";
import type { MealSuggestion } from "@/lib/models";
import { useHouseholdSubscription } from "@/hooks/useHouseholdSubscription";
import { LockedFeatureOverlay } from "@/components/features/LockedFeatureOverlay";
import { isFeatureUnlocked } from "@/lib/subscription";
import { userFacingMessage } from "@/lib/errors/userFacingError";

const CUISINE_OPTIONS = [
  "Pizza", "Chinese", "Thai", "Mexican", "Indian", "Japanese",
  "Korean", "Mediterranean", "Burgers", "BBQ", "Sushi", "Vietnamese",
] as const;

const MEAL_TYPES = ["packed_lunch", "breakfast", "lunch", "dinner"] as const;
const MEAL_TYPE_LABELS: Record<string, string> = {
  packed_lunch: "Packed Lunch",
  breakfast: "Breakfast",
  lunch: "Lunch",
  dinner: "Dinner",
};

export function MealsPage() {
  const { householdId, profile, loading: ctxLoading } = useHouseholdContext();
  const t = useTranslations();
  const { state: subState } = useHouseholdSubscription();
  // Meals are gated when the user has no active sub at all. Starter tier
  // unlocks dinner-only — but for the page-level gate we treat "any access"
  // as unlocked and the dinner-only limitation as a server-side concern.
  const mealsLocked = !isFeatureUnlocked("ai_meal_dinner", subState);

  const { data: allMeals, loading: mealsLoading } =
    useFirestoreCollection<MealSuggestion>(
      householdId ? `households/${householdId}/mealSuggestions` : null
    );

  const [expandedIngredients, setExpandedIngredients] = useState<Set<string>>(
    new Set()
  );
  const [expandedInstructions, setExpandedInstructions] = useState<Set<string>>(
    new Set()
  );
  const [regenerating, setRegenerating] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [refreshSnapshot, setRefreshSnapshot] = useState<{
    todayCount: number;
    startedAt: number;
  } | null>(null);
  const [showCustomForm, setShowCustomForm] = useState<string | null>(null);
  const [showTakeoutPicker, setShowTakeoutPicker] = useState<string | null>(null);
  const [customTitle, setCustomTitle] = useState("");
  const [customDescription, setCustomDescription] = useState("");
  const [customIngredients, setCustomIngredients] = useState("");

  const loading = ctxLoading || mealsLoading;
  // iOS treats `adult` and `elderly_parent` as adults for picking
  // meals / chosen state. `child` and `pet` cannot.
  const isAdult =
    profile?.memberType === "adult" || profile?.memberType === "elderly_parent";

  // Filter to today's meals
  // The Cloud Function stores suggested_date as midnight Pacific in UTC.
  // We must compute "today" in Pacific time to match.
  const todayMeals = useMemo(() => {
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

    return allMeals.filter((meal) => {
      return meal.suggested_date_str === todayStr;
    });
  }, [allMeals]);

  // Group by meal type. Within a group, sort chosen first, then favorites
  // (more upvotes than downvotes), then by created_at desc. Mirrors iOS
  // MealsView ordering so users see the household's pick at the top.
  const mealsByType = useMemo(() => {
    const groups: Record<string, MealSuggestion[]> = {};
    for (const type of MEAL_TYPES) {
      const list = todayMeals.filter((m) => m.meal_type === type);
      list.sort((a, b) => {
        const aChosen = a.is_chosen ? 1 : 0;
        const bChosen = b.is_chosen ? 1 : 0;
        if (aChosen !== bChosen) return bChosen - aChosen;
        const aVotes = a.votes ?? {};
        const bVotes = b.votes ?? {};
        const aScore =
          Object.values(aVotes).filter((v) => v === true).length -
          Object.values(aVotes).filter((v) => v === false).length;
        const bScore =
          Object.values(bVotes).filter((v) => v === true).length -
          Object.values(bVotes).filter((v) => v === false).length;
        if (aScore !== bScore) return bScore - aScore;
        return (b.created_at?.seconds ?? 0) - (a.created_at?.seconds ?? 0);
      });
      groups[type] = list;
    }
    return groups;
  }, [todayMeals]);

  const toggleIngredients = useCallback((mealId: string) => {
    setExpandedIngredients((prev) => {
      const next = new Set(prev);
      if (next.has(mealId)) {
        next.delete(mealId);
      } else {
        next.add(mealId);
      }
      return next;
    });
  }, []);

  const toggleInstructions = useCallback((mealId: string) => {
    setExpandedInstructions((prev) => {
      const next = new Set(prev);
      if (next.has(mealId)) {
        next.delete(mealId);
      } else {
        next.add(mealId);
      }
      return next;
    });
  }, []);

  // Returns today's local date string for suggested_date_str field
  function getTodayDateStr(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  }

  async function handleDeleteMeal(meal: MealSuggestion) {
    if (!meal.uid || !householdId) return;
    if (!window.confirm(`Delete "${meal.title}"?`)) return;
    await deleteDoc(doc(db, `households/${householdId}/mealSuggestions`, meal.uid));
  }

  async function handleAddCustomMeal(mealType: string) {
    if (!householdId || !profile?.uid || !customTitle.trim()) return;
    const ingredients = customIngredients
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    await addDoc(collection(db, `households/${householdId}/mealSuggestions`), {
      title: customTitle.trim(),
      description: customDescription.trim() || null,
      ingredients: ingredients.length > 0 ? ingredients : null,
      meal_type: mealType,
      source: "custom",
      suggested_date: Timestamp.now(),
      suggested_date_str: getTodayDateStr(),
      created_at: Timestamp.now(),
      created_by: profile.uid,
      is_chosen: false,
      is_skipped: false,
      votes: {},
    });
    setCustomTitle("");
    setCustomDescription("");
    setCustomIngredients("");
    setShowCustomForm(null);
  }

  async function handleAddTakeout(mealType: string, cuisine: string) {
    if (!householdId || !profile?.uid) return;
    await addDoc(collection(db, `households/${householdId}/mealSuggestions`), {
      title: `Order ${cuisine}`,
      description: `Takeout — ${cuisine}`,
      meal_type: mealType,
      source: "takeout",
      cuisine_type: cuisine,
      suggested_date: Timestamp.now(),
      suggested_date_str: getTodayDateStr(),
      created_at: Timestamp.now(),
      created_by: profile.uid,
      is_chosen: false,
      is_skipped: false,
      votes: {},
    });
    setShowTakeoutPicker(null);
  }

  async function handleVote(meal: MealSuggestion, isUpvote: boolean) {
    if (!meal.uid || !householdId || !profile?.uid) return;
    const ref = doc(
      db,
      `households/${householdId}/mealSuggestions`,
      meal.uid
    );
    const currentVote = meal.votes?.[profile.uid];
    // Toggle off if same vote, otherwise set
    if (currentVote === isUpvote) {
      const { [profile.uid]: _, ...rest } = meal.votes ?? {};
      await updateDoc(ref, { votes: rest });
    } else {
      await updateDoc(ref, { [`votes.${profile.uid}`]: isUpvote });
    }
  }

  async function handleChoose(meal: MealSuggestion) {
    if (!meal.uid || !householdId || !profile?.uid) return;
    const ref = doc(
      db,
      `households/${householdId}/mealSuggestions`,
      meal.uid
    );
    // Toggle: clicking the chosen button on an already-chosen meal
    // un-picks it (mirrors the iOS WeekendCard pattern). Mealtime
    // exclusivity (only one winner per meal_type) is handled when
    // making a new choice — we clear other winners in the same group.
    if (meal.is_chosen) {
      await updateDoc(ref, { is_chosen: false, chosen_by: null });
      return;
    }
    // Clear any other winner for the same meal_type in today's group
    // so the household sees a single canonical pick.
    const sameTypeWinners = todayMeals.filter(
      (m) =>
        m.meal_type === meal.meal_type &&
        m.is_chosen &&
        m.uid &&
        m.uid !== meal.uid,
    );
    for (const other of sameTypeWinners) {
      if (!other.uid) continue;
      await updateDoc(
        doc(db, `households/${householdId}/mealSuggestions`, other.uid),
        { is_chosen: false, chosen_by: null },
      );
    }
    await updateDoc(ref, {
      is_chosen: true,
      chosen_by: profile.uid,
    });
  }

  async function handleSkip(mealType: string) {
    if (!householdId) return;
    const mealsToSkip = mealsByType[mealType] ?? [];
    for (const meal of mealsToSkip) {
      if (!meal.uid) continue;
      const ref = doc(
        db,
        `households/${householdId}/mealSuggestions`,
        meal.uid
      );
      await updateDoc(ref, { is_skipped: true });
    }
  }

  async function handleUnskip(mealType: string) {
    if (!householdId) return;
    // Operate on the same set the page renders (todayMeals filters by
    // suggested_date_str, the canonical "today" key the Cloud Function
    // writes). Earlier this was a Timestamp-range filter against
    // browser-local-midnight, which silently dropped docs in any TZ that
    // didn't align with the CF's Pacific-midnight stamp — that made Undo
    // appear to do nothing.
    const mealsToUnskip = todayMeals.filter(
      (m) => m.meal_type === mealType && m.is_skipped,
    );
    for (const meal of mealsToUnskip) {
      if (!meal.uid) continue;
      const ref = doc(
        db,
        `households/${householdId}/mealSuggestions`,
        meal.uid
      );
      await updateDoc(ref, { is_skipped: false });
    }
  }

  async function handleRegenerate() {
    if (!householdId || regenerating) return;
    // Snapshot the current today-meal count so we can detect when the new
    // batch arrives via the snapshot listener and auto-clear the banner.
    const snapshot = { todayCount: todayMeals.length, startedAt: Date.now() };
    setRefreshSnapshot(snapshot);
    setRefreshError(null);
    setRegenerating(true);
    try {
      const functions = getFunctions();
      const regen = httpsCallable(functions, "regenerateHouseholdMeals");
      await regen({ householdId });
    } catch (error) {
      // iOS shows an alert here (`MealsView` regenerate failure path);
      // web previously logged + cleared the banner with no user-facing
      // signal, so the user just saw "nothing happened" and would
      // tap again. Surface a dismissible inline error instead.
      console.error("Error regenerating meals:", error);
      setRefreshSnapshot(null);
      setRefreshError(userFacingMessage(error, t("meals.refreshError.body")));
    } finally {
      setRegenerating(false);
    }
  }

  // Clear the "we're working on it" banner once new meal docs arrive (count
  // changed from when we kicked off) or after a 90s safety timeout.
  useEffect(() => {
    if (!refreshSnapshot) return;
    if (todayMeals.length !== refreshSnapshot.todayCount) {
      setRefreshSnapshot(null);
      return;
    }
    const elapsed = Date.now() - refreshSnapshot.startedAt;
    const remaining = Math.max(0, 90_000 - elapsed);
    const timer = setTimeout(() => setRefreshSnapshot(null), remaining);
    return () => clearTimeout(timer);
  }, [todayMeals.length, refreshSnapshot]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
          style={{
            borderColor: "var(--accent-color, #00C7BE)",
            borderTopColor: "transparent",
          }}
        />
      </div>
    );
  }

  return (
    <LockedFeatureOverlay
      locked={mealsLocked}
      title="Meal suggestions"
      description="Daily AI-curated meals from your groceries are a Zoey+ feature."
      paywallTitle="Unlock meal suggestions"
      paywallSubtitle="Zoey+ generates daily meal ideas from what's in your pantry and groceries."
    >
    <div className="p-4 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Meal Ideas</h1>
        <button
          onClick={handleRegenerate}
          disabled={regenerating || refreshSnapshot !== null}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-[color:var(--accent-color)] disabled:opacity-60 transition-all active:scale-95 active:opacity-80"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={regenerating || refreshSnapshot ? "animate-spin" : ""}
          >
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          {regenerating
            ? "Sending…"
            : refreshSnapshot
              ? "Refreshing…"
              : "Refresh Meals"}
        </button>
      </div>

      {refreshSnapshot && (
        <Card>
          <div className="flex items-center gap-3 py-1">
            <div
              className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin shrink-0"
              style={{
                borderColor: "var(--accent-color, #00C7BE)",
                borderTopColor: "transparent",
              }}
            />
            <p className="text-sm">
              <span className="font-medium">{t("meals.refreshing.title")}</span>{" "}
              <span className="text-gray-500">{t("meals.refreshing.body")}</span>
            </p>
          </div>
        </Card>
      )}

      {refreshError && (
        <Card>
          <div className="flex items-start gap-3 py-1">
            <span className="text-base shrink-0" aria-hidden="true">⚠️</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                {t("meals.refreshError.title")}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {refreshError}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setRefreshError(null)}
              aria-label={t("common.dismiss")}
              className="shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xs font-semibold"
            >
              ×
            </button>
          </div>
        </Card>
      )}

      {MEAL_TYPES.map((mealType) => {
        const meals = mealsByType[mealType] ?? [];
        const allSkipped =
          meals.length > 0 && meals.every((m) => m.is_skipped);
        const visibleMeals = meals.filter((m) => !m.is_skipped);

        return (
          <section key={mealType}>
            {/* Section header */}
            <div className="flex items-center justify-between px-1 mb-2">
              <h2 className="font-bold text-[length:var(--font-section-title)]">
                {MEAL_TYPE_LABELS[mealType] ?? mealType}
              </h2>
              {isAdult && meals.length > 0 && !allSkipped && (
                <button
                  onClick={() => handleSkip(mealType)}
                  className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                >
                  Skip
                </button>
              )}
            </div>

            {/* Skipped state */}
            {allSkipped ? (
              <Card>
                <div className="flex items-center justify-between py-2">
                  <p className="text-sm text-gray-400">
                    {MEAL_TYPE_LABELS[mealType] ?? mealType} skipped
                  </p>
                  <button
                    onClick={() => handleUnskip(mealType)}
                    className="text-sm font-medium"
                    style={{ color: "var(--accent-color, #00C7BE)" }}
                  >
                    Undo
                  </button>
                </div>
              </Card>
            ) : visibleMeals.length === 0 ? (
              <Card>
                <EmptyState
                  icon="🍽️"
                  title={`No ${(MEAL_TYPE_LABELS[mealType] ?? mealType).toLowerCase()} ideas yet`}
                />
              </Card>
            ) : (
              <div className="space-y-3">
                {visibleMeals.map((meal) => (
                  <MealCard
                    key={meal.uid}
                    meal={meal}
                    mealType={mealType}
                    isAdult={isAdult}
                    userId={profile?.uid}
                    ingredientsExpanded={expandedIngredients.has(
                      meal.uid ?? ""
                    )}
                    instructionsExpanded={expandedInstructions.has(
                      meal.uid ?? ""
                    )}
                    onToggleIngredients={() =>
                      toggleIngredients(meal.uid ?? "")
                    }
                    onToggleInstructions={() =>
                      toggleInstructions(meal.uid ?? "")
                    }
                    onVote={handleVote}
                    onChoose={handleChoose}
                    onDelete={handleDeleteMeal}
                    canDelete={
                      isAdult || meal.created_by === profile?.uid
                    }
                  />
                ))}
              </div>
            )}

            {/* Add custom meal / takeout */}
            {!allSkipped && (
              <div className="mt-3 space-y-2">
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowCustomForm(showCustomForm === mealType ? null : mealType);
                      setShowTakeoutPicker(null);
                    }}
                    className="flex-1 text-xs font-medium py-2 px-3 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                  >
                    + Suggest a {(MEAL_TYPE_LABELS[mealType] ?? mealType).toLowerCase()}
                  </button>
                  <button
                    onClick={() => {
                      setShowTakeoutPicker(showTakeoutPicker === mealType ? null : mealType);
                      setShowCustomForm(null);
                    }}
                    className="flex-1 text-xs font-medium py-2 px-3 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                  >
                    Order {(MEAL_TYPE_LABELS[mealType] ?? mealType).toLowerCase()}
                  </button>
                </div>

                {/* Custom meal form */}
                {showCustomForm === mealType && (
                  <Card>
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-gray-500">
                        Suggest a {(MEAL_TYPE_LABELS[mealType] ?? mealType).toLowerCase()}
                      </p>
                      <input
                        type="text"
                        value={customTitle}
                        onChange={(e) => setCustomTitle(e.target.value)}
                        placeholder="e.g. Mom's lasagna"
                        className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
                      />
                      <input
                        type="text"
                        value={customDescription}
                        onChange={(e) => setCustomDescription(e.target.value)}
                        placeholder={t("meals.custom.descriptionPlaceholder")}
                        className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
                      />
                      <textarea
                        value={customIngredients}
                        onChange={(e) => setCustomIngredients(e.target.value)}
                        placeholder={t("meals.custom.ingredientsPlaceholder")}
                        rows={3}
                        className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[color:var(--accent-color)] resize-none"
                      />
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => {
                            setShowCustomForm(null);
                            setCustomTitle("");
                            setCustomDescription("");
                            setCustomIngredients("");
                          }}
                          className="px-3 py-1.5 rounded-lg text-xs text-gray-500 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleAddCustomMeal(mealType)}
                          disabled={!customTitle.trim()}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-[color:var(--accent-color)] disabled:opacity-40 transition-opacity"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  </Card>
                )}

                {/* Takeout cuisine picker */}
                {showTakeoutPicker === mealType && (
                  <Card>
                    <p className="text-xs font-semibold text-gray-500 mb-2">
                      Pick a cuisine
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {CUISINE_OPTIONS.map((cuisine) => (
                        <button
                          key={cuisine}
                          onClick={() => handleAddTakeout(mealType, cuisine)}
                          className="px-2.5 py-1 rounded-full text-xs font-medium border border-orange-200 dark:border-orange-800 text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
                        >
                          {cuisine}
                        </button>
                      ))}
                    </div>
                  </Card>
                )}
              </div>
            )}
          </section>
        );
      })}
    </div>
    </LockedFeatureOverlay>
  );
}
